import { FC } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import { InvoiceStatus } from '__generated__/GQL';
import { SupportedLanguages } from 'common/locale';
import Button from 'components/_core/Button';
import { Copyable } from 'components/_core/Copyable';
import SunshineDropdown from 'components/_core/SunshineDropdown';
import Text from 'components/_core/Text';
import { logEvent } from 'features/Analytics/analytics';
import DownloadPdfButton, {
  LanguageOptionsType,
} from 'features/Work/components/DownloadPdfButton';
import { useClientContacts } from 'features/Work/hooks/useClientContacts';
import { Invoice } from 'features/Work/pages/Invoices/hooks/queries/useGetInvoice';
import * as routes from 'features/Work/routes';
import { downloadPdf } from 'features/Work/utils';
import { buildPaymentLink } from 'features/Work/utils/buildUrl';

import { useInvoiceModals } from '../../../../../../hooks/useInvoiceModals/';
import locales from './locales';

interface TopbarActionsProps {
  clientName: string;
  invoice: Invoice;
  lang: SupportedLanguages;
  openResendWithShineModal: () => void;
  setLang: (lang: LanguageOptionsType['value']) => void;
}

const INVOICE_FAQ_URL = 'https://help.shine.fr/fr/?q=facture';

const TopbarActions: FC<TopbarActionsProps> = ({
  clientName,
  invoice,
  lang,
  openResendWithShineModal,
  setLang,
}) => {
  const { openModal } = useInvoiceModals();
  const [contactOptions] = useClientContacts();
  const history = useHistory();
  const hasContacts = contactOptions.clientContactIds.length > 0;
  const isAlreadySent = invoice.status !== InvoiceStatus.Pending;
  const publicPaymentUrl = buildPaymentLink({
    lang,
    publicPaymentUrl: invoice.publicPaymentUrl,
  });

  const onDownloadPdf = (pdfLang: LanguageOptionsType['value']) => {
    logEvent({
      name: 'Download Invoice',
      properties: {
        from: 'InvoiceDetailsPage',
        invoiceId: invoice.invoiceId,
        lang: pdfLang,
      },
    });

    if (isAlreadySent) {
      downloadPdf({ lang: pdfLang, pdf: invoice.pdf });
      return;
    }

    setLang(pdfLang);

    openModal({
      name: 'download',
      props: { ...invoice, lang: pdfLang },
    });
  };

  const isNotCanceled = invoice.status !== InvoiceStatus.Canceled && clientName;

  return (
    <>
      <Button
        as="a"
        href={INVOICE_FAQ_URL}
        minWidth="inherit"
        onClick={() => logEvent({ name: 'Open Invoice FAQ' })}
        rel="noopener noreferrer"
        target="_blank"
        variant="inline-primary"
      >
        {locales.help}
      </Button>

      {isAlreadySent ? (
        <Button
          onClick={() => openModal({ name: 'duplicate', props: invoice })}
          variant="inline-primary"
        >
          {locales.duplicate}
        </Button>
      ) : (
        <>
          <Button
            onClick={() =>
              openModal({
                name: 'delete',
                props: {
                  ...invoice,
                  onDeleteSuccess: () => history.replace(routes.invoices),
                },
              })
            }
            variant="inline-primary"
          >
            {locales.delete}
          </Button>

          <Button
            as={Link}
            to={routes.getEditInvoice(invoice.invoiceId)}
            variant="inline-primary"
          >
            {locales.edit}
          </Button>
        </>
      )}

      {isNotCanceled ? (
        <>
          {isAlreadySent ? (
            <Copyable text={publicPaymentUrl}>
              <Button variant="inline-primary">
                <Text noOfLines={1} title={locales.copyLink} variant="primary">
                  {locales.copyLink}
                </Text>
              </Button>
            </Copyable>
          ) : null}

          <DownloadPdfButton
            item={invoice}
            onClickItem={(pdfLang) => onDownloadPdf(pdfLang)}
            title={locales.download}
            variant="inline-primary"
          />

          {isAlreadySent ? (
            <SunshineDropdown>
              <SunshineDropdown.Trigger>
                <Button icon="chevron-down" isDisabled={!hasContacts}>
                  {locales.formatString(locales.send, clientName)}
                </Button>
              </SunshineDropdown.Trigger>

              <SunshineDropdown.List>
                <SunshineDropdown.Item onClick={openResendWithShineModal}>
                  {locales.sendWithShine}
                </SunshineDropdown.Item>

                <SunshineDropdown.Item
                  onClick={() =>
                    openModal({
                      name: 'resend-by-myself',
                      props: {
                        invoiceId: invoice.invoiceId,
                        lang,
                        pdf: invoice.pdf,
                        publicPaymentUrl: invoice.publicPaymentUrl,
                      },
                    })
                  }
                >
                  {locales.sendByMyself}
                </SunshineDropdown.Item>
              </SunshineDropdown.List>
            </SunshineDropdown>
          ) : (
            <Button
              icon="send"
              isDisabled={!hasContacts}
              onClick={() =>
                openModal({
                  name: 'send',
                  props: { ...invoice, contactOptions },
                })
              }
            >
              {locales.formatString(locales.send, clientName)}
            </Button>
          )}
        </>
      ) : null}
    </>
  );
};

export default TopbarActions;
